import dayjs from 'dayjs'
import { OptionalTimeZone, TimeZone } from '../constants/timezones'

/**
 * Creates a DateFormatter instance with the specified timezone.
 *
 * This factory function is exported instead of the DateFormatter class to allow for
 * easier "1-line" use of DateFormatter's utility methods.
 *
 * @param tz The timezone to use for formatting dates. If null or undefined, the
 * system's local timezone is used. However, when rendering dates on the server, you
 * should nearly always pass the associated project's timezone.
 *
 * @example
 * console.log(dateFormatter("America/Denver").simple(new Date())); // Outputs date in MM/DD/YYYY format.
 */
export const dateFormatter = (tz: OptionalTimeZone) =>
  new DateFormatter(tz ?? null)

class DateFormatter {
  constructor(private tz: TimeZone | null) {}
  /**
   * Formats a nodejs Date as a string using dayjs and a valid dayjs template
   *
   * If no format is supplied, it will return the string representation of a nodejs Date
   * @param date a nodejs Date to be formatted. Can be a Date object or ISO 8601 string.
   * @param template the desired format of the Date
   * @returns a formatted string
   * @see {@link https://day.js.org/docs/en/display/format}
   * @example
   * const date = new Date('2019-01-25') // with time portion
   * dateFormatter("America/New_York").custom(date) // 'Fri, 25 Jan 2019 02:23:53 GMT'
   * dateFormatter("America/New_York").custom(date, 'MMMM D, YYYY hh:mm:SSS a') // 'January 25, 2019 02:23:532 am'
   */
  custom = (date: Date | string | null | undefined, template: string) =>
    this.format(date!, template)

  /**
   * Formats a nodejs Date as a string using dayjs with the `MM/DD/YYYY` format
   * @param date the date to format. Can be a Date object or ISO 8601 string.
   * @returns a formatted string
   * @example
   * const date = new Date('2022-01-01')
   * dateFormatter("America/New_York").simple(date) // '01/01/2022'
   */
  simple = (date: Date | string | null | undefined) =>
    this.format(date, 'MM/DD/YYYY')
  /**
   * Formats a nodejs Date as a string using dayjs with the `YYYY-MM-DD` ISO format
   * @param date the date to format. Can be a Date object or ISO 8601 string.
   * @returns a formatted string
   * @example
   * const date = new Date('2022-01-01')
   * dateFormatter("America/New_York").simpleISO(date) // '2022-01-01'
   */
  simpleISO = (date: Date | string | null | undefined) =>
    this.format(date, 'YYYY-MM-DD')
  /**
   * Formats a nodejs Date as a string using dayjs with the `MM/DD/YY` format
   * @param date the date to format. Can be a Date object or ISO 8601 string.
   * @returns a formatted string
   * @example
   * const date = new Date('2022-01-01')
   * dateFormatter("America/New_York").short(date) // '01/01/22'
   */
  short = (date: Date | string | null | undefined) =>
    this.format(date, 'MM/DD/YY')
  /**
   * Formats a nodejs Date as a string using dayjs with the `YYYYMMDD` format
   * @param date the date to format.  Can be a Date object or ISO 8601 string.
   * @returns a formatted string
   * @example
   * const date = new Date('2022-01-01')
   * dateFormatter("America/New_York").file(date) // '20220101'
   */
  file = (date: Date | string | null | undefined) =>
    this.format(date, 'YYYYMMDD')
  /**
   * Formats a nodejs Date as a string using dayjs with the `YYYYMMDD-HHmmSSS` format
   * @param date the date to format. Can be a Date object or ISO 8601 string.
   * @returns a formatted string
   * @example
   * const date = new Date('2022-01-01')
   * dateFormatter("America/New_York").longFile(date) // 20220101-0000000
   */
  longFile = (date: Date | string | null | undefined) =>
    this.format(date, 'YYYYMMDD-HHmmSSS')
  /**
   * Formats a nodejs Date as a string using dayjs with the `MM/DD/YYYY h:mmA` format
   * @param date the date to format. Can be a Date object or ISO 8601 string.
   * @returns a formatted string
   * @example
   * const date = new Date('2022-01-01') // with time portion
   * dateFormatter("America/New_York").datetime(date) // '01/01/2022 7:32PM'
   */
  datetime = (date: Date | string | null | undefined) =>
    this.format(date, 'MM/DD/YYYY h:mmA')
  /**
   * Formats a nodejs Date as a string using dayjs with the `MM/DD/YY h:mmA` format
   * @param date the date to format. Can be a Date object or ISO 8601 string.
   * @returns a formatted string
   * @example
   * const date = new Date('2022-01-01') // with time portion
   * dateFormatter("America/New_York").short(date) // '01/01/22 4:20AM'
   */
  shortDatetime = (date: Date | string | null | undefined) =>
    this.format(date, 'MM/DD/YY h:mmA')
  /**
   * Formats a nodejs Date as a string using dayjs with the `YYYY-MM-DD HH:mm:SSS` format
   * @param date the date to format. Can be a Date object or ISO 8601 string.
   * @returns a formatted string
   */
  longDatetime = (date: Date | string | null | undefined) =>
    this.format(date, 'YYYY-MM-DD HH:mm:SSS')

  private format = (date: Date | string | null | undefined, format: string) => {
    if (!date) return ''
    return dayjs(date)
      .tz(this.tz ?? undefined)
      .format(format)
  }
}
